import { useCallback, useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';

import { searchUserByAvailableSections } from '~modules/user/api';
import { UserDto } from '~modules/user/model';

interface UserSelectProps {
	value?: UserDto[] | UserDto;
	onChange?: (users: UserDto[] | UserDto) => void;
	placeholder?: string;
	isDisabled?: boolean;
	ignoreSections?: boolean;
	isMulti?: boolean;
	className?: string;
	selectClassName?: string;
}

export const UserSelect = ({
	value = [],
	onChange,
	placeholder = 'Выберите пользователей...',
	isDisabled = false,
	ignoreSections = false,
	isMulti = false,
	className,
	selectClassName,
}: UserSelectProps) => {
	const [inputValue, setInputValue] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [defaultOptions, setDefaultOptions] = useState<UserDto[]>([]);

	useEffect(() => {
		setIsLoading(true);
		searchUserByAvailableSections('', 0, false)
			.then(v => setDefaultOptions(v))
			.catch(console.error)
			.finally(() => {
				setIsLoading(false);
			});
	}, []);

	const loadOptions = useCallback(
		async (inputText: string): Promise<UserDto[]> => {
			setIsLoading(true);
			try {
				return await searchUserByAvailableSections(inputText, 0, ignoreSections);
			} catch (error) {
				console.error('Ошибка загрузки пользователей:', error);
				return [];
			} finally {
				setIsLoading(false);
			}
		},
		[ignoreSections],
	);

	const handleChange = (selectedOptions: readonly UserDto[] | null | UserDto) => {
		if (isMulti) {
			const selectedUsers = selectedOptions ? [...(selectedOptions as UserDto[])] : [];
			onChange?.(selectedUsers);
		} else {
			const selectedUser = selectedOptions as UserDto;
			onChange?.(selectedUser);
		}
	};

	const containerClassName = `user-select${className ? ` ${className}` : ''}`;
	const asyncSelectClassName = `react-select${selectClassName ? ` ${selectClassName}` : ''}`;

	return (
		<div className={containerClassName}>
			<AsyncSelect<UserDto, boolean>
				className={asyncSelectClassName}
				classNamePrefix="custom-select"
				value={value}
				getOptionLabel={option => `${option.name}`}
				getOptionValue={option => `${option.id}`}
				isMulti={isMulti}
				isDisabled={isDisabled}
				isLoading={isLoading}
				defaultOptions={defaultOptions}
				loadOptions={loadOptions}
				onInputChange={newValue => {
					setInputValue(newValue);
					return newValue;
				}}
				onChange={handleChange}
				placeholder={placeholder}
				noOptionsMessage={({ inputValue }) =>
					inputValue ? 'Пользователи не найдены' : 'Введите имя для поиска'
				}
				loadingMessage={() => 'Поиск пользователей...'}
				inputValue={inputValue}
				styles={{
					control: base => ({
						...base,
						minHeight: '42px',
					}),
				}}
			/>
		</div>
	);
};
